import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { useToast } from "@/components/Toast";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useEmployeesAccessData } from "@/hooks/useEmployeesAccessData";
import { useLoggedInEmployee } from "@/hooks/useLoggedInEmployee";
import { db } from "@/lib/supabase";
import { type MouseEvent, useEffect, useMemo, useState } from "react";
import { getEmployeeDisplayName } from "@/utils/nameUtils";

const MIN_USERS = 2;

export function SettingsAccessTable() {
  const { employees, loading, error } = useEmployeesAccessData();
  const { employee: self } = useLoggedInEmployee();
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const { addToast } = useToast();

  const [search, setSearch] = useState("");
  // employeeId -> has-access, keyed over ALL employees (not just the filtered
  // view) so edits to rows scrolled/filtered out of sight are preserved.
  // savedAccess = last-saved state; pendingAccess = unsaved edits. On Save we
  // advance savedAccess locally rather than refetching, which avoids a flicker.
  const [savedAccess, setSavedAccess] = useState<Map<string, boolean>>(new Map());
  const [pendingAccess, setPendingAccess] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);

  const activeEmployees = useMemo(
    () => employees.filter((employee) => employee.is_active !== false),
    [employees]
  );

  // Seed both maps when the server data loads. We don't subscribe to realtime
  // here, so this only fires on the initial fetch and won't clobber unsaved edits.
  useEffect(() => {
    const map = new Map(activeEmployees.map((e) => [e.id, e.is_user]));
    setSavedAccess(map);
    setPendingAccess(map);
  }, [activeEmployees]);

  // User Access is an org-wide admin surface: every active employee is eligible to
  // be granted app access, regardless of which office(s) the global header filter
  // has selected. So this table intentionally ignores that filter (unlike the
  // boards) and only narrows the list by the name search below.
  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeEmployees;
    return activeEmployees.filter((employee) => {
      const displayName = getEmployeeDisplayName(employee).toLowerCase();
      const canonicalName = (employee.name ?? "").toLowerCase();
      return displayName.includes(term) || canonicalName.includes(term);
    });
  }, [activeEmployees, search]);

  const usersCount = useMemo(
    () => [...pendingAccess.values()].filter(Boolean).length,
    [pendingAccess]
  );

  const changedIds = useMemo(
    () => [...pendingAccess].filter(([id, v]) => savedAccess.get(id) !== v).map(([id]) => id),
    [pendingAccess, savedAccess]
  );
  const dirty = changedIds.length > 0;
  // During the initial load pendingAccess is still empty, so usersCount is 0 and this would
  // be true — gate on !loading so the "At least N users…" hint/validation doesn't flash
  // before employees have resolved.
  const belowMinimum = !loading && usersCount < MIN_USERS;

  const handleToggle = (employeeId: string, value: boolean) => {
    if (!value && usersCount - 1 < MIN_USERS) {
      addToast(`At least ${MIN_USERS} users must have access.`, "warning");
      return;
    }
    setPendingAccess((prev) => {
      const next = new Map(prev);
      next.set(employeeId, value);
      return next;
    });
  };

  const handleSave = async () => {
    if (usersCount < MIN_USERS) {
      addToast(`At least ${MIN_USERS} users must have access.`, "warning");
      return;
    }

    setSaving(true);
    try {
      const results = await Promise.all(
        changedIds.map((id) => {
          markSelfEdit("employees", id);
          return db.employees.update(id, { is_user: pendingAccess.get(id)! });
        })
      );
      const failed = results.find((r) => r.error);
      if (failed) {
        addToast("Failed to save some changes. Please retry.", "error");
        return;
      }
      // The pending edits are now the saved state (no refetch, so no flicker).
      setSavedAccess(new Map(pendingAccess));
      addToast("Access updated.", "success");
    } catch {
      addToast("Failed to save changes.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRowClick = (
    e: MouseEvent,
    employeeId: string,
    checked: boolean,
    isSelf: boolean
  ) => {
    if (isSelf) return;
    // Clicks landing on the checkbox itself are handled by its onChange; ignore
    // them here so the toggle doesn't fire twice.
    if ((e.target as HTMLElement).closest("input")) return;
    handleToggle(employeeId, !checked);
  };

  return (
    <section className="settings-section">
      <div className="settings-section__panel">
      <div className="settings-section__header">
        <input
          type="text"
          className="settings-section__search"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search employees by name"
        />
        <div className="settings-section__actions">
          {belowMinimum && (
            <span className="settings-section__hint" role="alert">
              At least {MIN_USERS} users must have access.
            </span>
          )}
          <button
            type="button"
            className="primary-button"
            disabled={!dirty || saving || belowMinimum}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} entityName="employees" />}

      {loading ? (
        <LoadingState message="Loading employees…" />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table settings-table--access">
            <thead>
              <tr>
                <th className="settings-table__th">Name</th>
                <th className="settings-table__th">Office</th>
                <th className="settings-table__th settings-table__th--access">Has Access</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={3} className="settings-table__empty">
                    No employees found matching the current filters.
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => {
                  const isSelf = employee.id === self?.id;
                  const checked = pendingAccess.get(employee.id) ?? employee.is_user;
                  return (
                    <tr
                      key={employee.id}
                      className={`settings-table__row${
                        isSelf ? "" : " settings-table__row--clickable"
                      }`}
                      onClick={(e) => handleRowClick(e, employee.id, checked, isSelf)}
                    >
                      <td className="settings-table__cell">
                        {getEmployeeDisplayName(employee)}
                      </td>
                      <td className="settings-table__cell">{employee.office?.name ?? "—"}</td>
                      <td className="settings-table__cell settings-table__cell--access">
                        <input
                          type="checkbox"
                          className="settings-table__checkbox"
                          checked={checked}
                          disabled={isSelf}
                          title={isSelf ? "You can't change your own access" : undefined}
                          onChange={(e) => handleToggle(employee.id, e.target.checked)}
                          aria-label={`Access for ${getEmployeeDisplayName(employee) || "employee"}`}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </section>
  );
}
